/**
 * AI Service Module
 * 
 * This module provides client-side AI capabilities by calling the backend server API.
 */

// Helper to determine the API base URL depending on environment
const getApiUrl = (path) => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `http://localhost:3001${path}`;
  } else if (hostname === 'newreports.apexionx.com') {
    return `https://reportsnew.apexionx.com${path}`;
  }
  return path; // relative path in other environments
};

/**
 * Creates a standardized error response object
 */
function createErrorResponse(type, message, model = null) {
    return {
        id: `error-${type}-${Date.now()}`,
        text: `Error: ${message}`,
        model: model || 'default',
        error: true
    };
}

/**
 * Get AI suggestions for improving report text
 * 
 * @param {string} reportText - The original report text to improve
 * @param {string} [model=null] - The AI model to use
 * @returns {Promise<Object>} - The improved text suggestion
 */
export async function getAISuggestions(reportText, model = null) {
    try {
        const apiUrl = getApiUrl('/api/ai/suggestions');
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ reportText, model }),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Server error: ${response.status} - ${errorText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting AI suggestions:', error);
        return createErrorResponse('suggestions', error.message, model);
    }
}

/**
 * Get AI review of the report with HTML formatting
 * 
 * @param {string} reportText - The report text to review
 * @param {string} [model=null] - The AI model to use
 * @returns {Promise<Object>} - The AI-generated review with HTML formatting
 */
export async function getAIReview(reportText, model = null) {
    try {
        const apiUrl = getApiUrl('/api/ai/review');
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ reportText, model }),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Server error: ${response.status} - ${errorText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting AI review:', error);
        const errorHtml = `<!DOCTYPE html><html><head><title>Error</title></head><body><p>Error fetching review: ${error.message}</p></body></html>`;
        return {
            id: `error-review-${Date.now()}`,
            html: errorHtml,
            model: model || 'default',
            error: true
        };
    }
}

/**
 * Get lawyer review simulation with HTML formatting
 * 
 * @param {string} reportText - The report text to review from a legal perspective
 * @param {string} [model=null] - The AI model to use
 * @returns {Promise<Object>} - The AI-generated legal review with HTML formatting
 */
export async function getLawyerReview(reportText, model = null) {
    try {
        const apiUrl = getApiUrl('/api/ai/lawyer-review');
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ reportText, model }),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Server error: ${response.status} - ${errorText}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting lawyer review:', error);
        const errorHtml = `<!DOCTYPE html><html><head><title>Error</title></head><body><p>Error fetching lawyer review: ${error.message}</p></body></html>`;
        return {
            id: `error-lawyer-${Date.now()}`,
            html: errorHtml,
            model: model || 'default',
            error: true
        };
    }
}

/**
 * Get AI-powered rewrite of selected text
 * 
 * @param {string} selectedText - The text to rewrite
 * @param {string} [context=''] - Additional context
 * @param {string} [instructions=''] - Specific instructions
 * @param {string} [model=null] - The AI model to use
 * @returns {Promise<Object>} - The rewritten text result
 */
export async function getAIRewrite(selectedText, context = '', instructions = '', model = null) {
  try {
    const apiUrl = getApiUrl('/api/ai/rewrite');
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ selectedText, context, instructions, model }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server error: ${response.status} - ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting AI rewrite:', error);
    return createErrorResponse('rewrite', error.message, model);
  }
}

/**
 * Get AI-generated ghost text suggestion (auto-completion)
 * 
 * @param {string} text - The current text to generate suggestions for
 * @returns {Promise<string>} - The suggestion text
 */
export async function getGhostTextSuggestion(text) {
    try {
        const apiUrl = getApiUrl('/api/ai/ghost-text');
        
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ text }),
        });
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return data.suggestion || '';
    } catch (error) {
        console.error('Error fetching ghost text suggestion:', error);
        return '';
    }
}

/**
 * Get AI-generated draft from the backend service
 * 
 * @param {string|Object} formData - The form data or topic string
 * @param {Array} [history=[]] - Conversation history
 * @returns {Promise<string>} - The generated draft
 */
export async function getAIDraft(formData, history = []) {
  try {
    const apiUrl = getApiUrl('/api/ai/agent-draft');
    
    // If formData is a string (legacy), convert it to the new format
    let requestData;
    if (typeof formData === 'string') {
      requestData = { topic: formData, history };
    } else {
      // Format the structured data for the AI agent
      const reportDetails = [];
      if (formData.suspects) reportDetails.push(`Suspect(s): ${formData.suspects}`);
      if (formData.victims) reportDetails.push(`Victim(s): ${formData.victims}`);
      if (formData.location) reportDetails.push(`Location: ${formData.location}`);
      if (formData.date) reportDetails.push(`Date: ${formData.date}`);
      if (formData.offense) reportDetails.push(`Offense: ${formData.offense}`);
      if (formData.summary) reportDetails.push(`Summary: ${formData.summary}`);
      
      const topic = reportDetails.length > 0 
        ? `Generate a police report with the following details:\n${reportDetails.join('\n')}`
        : 'Generate a basic police report template';
        
      requestData = { topic, history, formData };
    }
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network response was not ok' }));
      throw new Error(errorData.message || 'Network response was not ok');
    }

    const data = await response.json();
    return data.draft || 'Unable to generate draft at this time.';
  } catch (error) {
    console.error('Error fetching AI draft:', error);
    return `Error: Could not generate draft. ${error.message}`;
  }
}

/**
 * Get AI-generated case map from report text
 * 
 * @param {string} reportText - The report text to map
 * @param {string} [model=null] - The AI model to use
 * @returns {Promise<Object>} - The case map with nodes and edges
 */
export async function getAIMappedCase(reportText, model = null) {
  try {
    const apiUrl = getApiUrl('/api/ai/map-case');
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reportText, model }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network response was not ok' }));
      throw new Error(errorData.message || 'Network response was not ok');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching AI case map:', error);
    return { nodes: [], edges: [], error: error.message };
  }
}

/**
 * AI-powered next steps analysis for detective work
 * 
 * @param {string} reportText - The report text to analyze
 * @param {string} [model=null] - The AI model to use
 * @returns {Promise<Object>} - The next steps analysis
 */
export async function getAINextSteps(reportText, model = null) {
  try {
    const apiUrl = getApiUrl('/api/ai/next-steps');
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reportText, model }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Network response was not ok' }));
      throw new Error(errorData.message || 'Network response was not ok');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching AI next steps analysis:', error);
    return {
      completed: [],
      inProgress: [],
      nextSteps: [],
      questions: [],
      interviews: [],
      locations: [],
      error: error.message
    };
  }
}

/**
 * Attempts to use a fallback model (for backward compatibility)
 */
export async function withFallbackModel(apiCall, ...args) {
    try {
        return await apiCall(...args);
    } catch (error) {
        console.error('Error in primary model call:', error);
        throw error;
    }
}